import { ExitIcon } from '@radix-ui/react-icons';
import {
  Box,
  Button,
  Flex,
  Heading,
  RadioCards,
  Spinner,
  Text,
} from '@radix-ui/themes';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout } from '@/entities/session';
import {
  refreshAccessToken,
  useLazyStaffStoreAppDetailsQuery,
  useStaffStoresQuery,
  updateGlobalAPIBaseURLs,
} from '@/shared/api';
import { useAppDispatch, useRedirectPath } from '@/shared/hooks';
import {
  getAppKey,
  convertServicesToEnvironment,
  appSession,
} from '@/shared/lib';

export function Stores() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const appKey = getAppKey();
  const { data, isLoading } = useStaffStoresQuery({ app_key: appKey });
  const [loadingShop, setLoadingShop] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [getAppDetails] = useLazyStaffStoreAppDetailsQuery();
  const { navigateToLoginAfterLogout } = useRedirectPath();

  const onSelect = async (shopKey: string) => {
    setLoadingShop(shopKey);

    try {
      const appDetails = await getAppDetails(
        { store_key: shopKey, app_key: appKey },
        false
      ).unwrap();

      if (appDetails) {
        await refreshAccessToken();

        const services = convertServicesToEnvironment(
          appDetails.result.services
        );
        updateGlobalAPIBaseURLs(services);

        navigate(`/stores/${shopKey}`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingShop(null);
    }
  };

  return (
    <Flex height="100dvh" width="100%" justify="center" align="center">
      <Box className="space-y-4">
        <Heading align="center">Select store</Heading>

        <Button
          disabled={loading}
          className="absolute top-0 right-8"
          variant="ghost"
          color="gray"
          onClick={() => {
            setLoading(true);
            dispatch(logout())
              .unwrap()
              .then(() => {
                setLoading(false);
                appSession.removeItem();
                navigateToLoginAfterLogout();
              });
          }}
        >
          <ExitIcon /> Logout
        </Button>

        <Box>
          {isLoading ? (
            <Flex justify="center" align="center" className="w-full">
              <Spinner />
            </Flex>
          ) : data?.results.length === 0 ? (
            <Flex justify="center" align="center" className="w-full">
              <Text color="gray">No stores found</Text>
            </Flex>
          ) : (
            <RadioCards.Root
              onValueChange={onSelect}
              className="flex justify-center flex-wrap gap-4"
            >
              {data?.results.map((shop) => (
                <RadioCards.Item
                  value={shop.key}
                  key={shop.id}
                  className="min-w-[200px] basis-[calc(100%/3-1rem)]"
                >
                  {loadingShop === shop.key && (
                    <Flex
                      justify="center"
                      align="center"
                      className="absolute inset-0 bg-white/60"
                    >
                      <Spinner />
                    </Flex>
                  )}
                  <Flex direction="column" width="100%">
                    <Text weight="bold">{shop.name}</Text>
                    <Text>{shop.address}</Text>
                  </Flex>
                </RadioCards.Item>
              ))}
            </RadioCards.Root>
          )}
        </Box>
      </Box>
    </Flex>
  );
}
